import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState } from './stores/types/types';
import * as Storage from './utils/local-storage';
import * as SessionActions from './stores/actions/session';
import JwtDecode from 'jwt-decode';
import type { ShowroomsTokens } from './stores/types/session';

const FIVE_MINUTES_SECONDS = 300;

export default function ShowroomsTokensAutoRefresh() {
	const showroomsTokens = useSelector((state: GreenroomState) => state.SessionReducer.showroomsTokens);

	const refreshInterval = useRef(undefined as NodeJS.Timeout | undefined)
	const dispatch = useDispatch();

	async function refreshToken() {
		const showroomsTokensJWT = Storage.getStorageItem('showrooms-tokens');
		dispatch(SessionActions.refreshShowroomsTokens(showroomsTokensJWT));
	}

	// UNCOMMENT THIS FUNCTION TO TEST REFRESHING TOKEN ON EVERY BROWSER REFRESH
	// useEffect(() => {
	// 	const tokens = Storage.getStorageItem('showrooms-tokens')
	// 	dispatch(SessionActions.refreshShowroomsTokens(tokens));
	// }, [])

	function setIntervalToAutoRefresh() {
		const lastRefreshDate = new Date(showroomsTokens!.lastRefresh);
		const fiveMinsUntilTokenExpires = new Date(lastRefreshDate.getTime() + ((showroomsTokens!.expiresIn - FIVE_MINUTES_SECONDS) * 1000));
		const millisecondsUntilThen = fiveMinsUntilTokenExpires.getTime() - new Date().getTime();
		// UNCOMMENT THIS LINE TO SEE THAT THE TIMER IS WORKING TO AUTO REFRESH WHEN WITHIN 5 MINS
		//console.log(`mins until auto refresh token: ${millisecondsUntilThen / 1000 / 60}`);
		refreshInterval.current = setTimeout(() => {
			clearInterval(refreshInterval.current!);
			refreshInterval.current = undefined;
			refreshToken();
		}, millisecondsUntilThen)
	}

	useEffect(() => {
		// Load tokens from local storage when app loads
		const tokens = JwtDecode(Storage.getStorageItem('showrooms-tokens')) as ShowroomsTokens;
		dispatch(SessionActions.setShowroomsTokens(tokens));
	}, [])

	useEffect(() => {
		// This fires whenever the tokens change. The tokens change when they are loaded from local storage (useEffect above ^),
		// when the user logs in, and when the tokens refresh.
		if (showroomsTokens?.lastRefresh) {
			const secondsFromLastRefresh = (new Date().getTime() - new Date(showroomsTokens.lastRefresh).getTime()) / 1000;
			if (secondsFromLastRefresh > (showroomsTokens.expiresIn - FIVE_MINUTES_SECONDS)) {
				// Within 5 minutes of expiring or already expired, refresh token
				refreshToken();
			} else {
				// Set up interval to preemptively refresh token when 5 mins away from token expiring
				if (!refreshInterval.current) {
					setIntervalToAutoRefresh();
				}
			}
		}
	}, [showroomsTokens])

	return null;
}